import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { ScopeMode, SphereSubscriptionWithChannel, EffectiveSubscription } from 'types/channel';
import {
  listSphereSubscriptions,
  getEffectiveSphereSubscriptions,
  subscribeSphereToChannel,
  unsubscribeSphereFromChannel,
  updateSphereSubscriptionScope,
} from '../api/marketplaceApi';

export interface UseSphereSubscriptionsResult {
  effectiveSubscriptions: EffectiveSubscription[];
  directSubscriptions: SphereSubscriptionWithChannel[];
  loading: boolean;
  error: string | null;
  isMutating: boolean;
  subscribe: (channelId: string, scopeMode: ScopeMode) => Promise<boolean>;
  unsubscribe: (subscriptionId: string) => Promise<boolean>;
  updateScope: (subscriptionId: string, scopeMode: ScopeMode) => Promise<boolean>;
  refetch: () => void;
}

export function useSphereSubscriptions(
  sphereId: string | null
): UseSphereSubscriptionsResult {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [effectiveSubscriptions, setEffectiveSubscriptions] = useState<EffectiveSubscription[]>([]);
  const [directSubscriptions, setDirectSubscriptions] = useState<SphereSubscriptionWithChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [isMutating, setIsMutating] = useState(false);

  const refetch = useCallback(() => {
    setFetchTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!apiClients || !sphereId) {
      setEffectiveSubscriptions([]);
      setDirectSubscriptions([]);
      return;
    }

    let cancelled = false;

    const fetchSubscriptions = async () => {
      setLoading(true);
      setError(null);

      try {
        const [effectiveRes, directRes] = await Promise.all([
          getEffectiveSphereSubscriptions(apiClients.schemaClient, sphereId),
          listSphereSubscriptions(apiClients.schemaClient, sphereId),
        ]);

        if (!cancelled) {
          setEffectiveSubscriptions(effectiveRes.data);
          setDirectSubscriptions(directRes.data);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : t('sphereChannels.errors.loadFailed');
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchSubscriptions();

    return () => {
      cancelled = true;
    };
  }, [apiClients, sphereId, fetchTrigger, t]);

  const subscribe = useCallback(
    async (channelId: string, scopeMode: ScopeMode): Promise<boolean> => {
      if (!apiClients || !organizationId || !sphereId) return false;

      setIsMutating(true);
      setError(null);

      try {
        await subscribeSphereToChannel(
          apiClients.schemaClient,
          channelId,
          sphereId,
          scopeMode
        );
        refetch();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : t('sphereChannels.addChannel.error');
        setError(message);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, organizationId, sphereId, refetch, t]
  );

  const unsubscribe = useCallback(
    async (subscriptionId: string): Promise<boolean> => {
      if (!apiClients || !sphereId) return false;

      setIsMutating(true);
      setError(null);

      const previousDirect = [...directSubscriptions];
      setDirectSubscriptions((prev) => prev.filter((s) => s.id !== subscriptionId));

      try {
        await unsubscribeSphereFromChannel(
          apiClients.schemaClient,
          subscriptionId,
          sphereId
        );
        refetch();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : t('sphereChannels.errors.unsubscribeFailed');
        setError(message);
        setDirectSubscriptions(previousDirect);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, sphereId, directSubscriptions, refetch, t]
  );

  const updateScope = useCallback(
    async (subscriptionId: string, scopeMode: ScopeMode): Promise<boolean> => {
      if (!apiClients || !sphereId) return false;

      setIsMutating(true);
      setError(null);

      const previousDirect = [...directSubscriptions];
      setDirectSubscriptions((prev) =>
        prev.map((s) => (s.id === subscriptionId ? { ...s, scope_mode: scopeMode } : s))
      );

      try {
        await updateSphereSubscriptionScope(
          apiClients.schemaClient,
          subscriptionId,
          scopeMode,
          sphereId
        );
        refetch();
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : t('sphereChannels.errors.updateFailed');
        setError(message);
        setDirectSubscriptions(previousDirect);
        return false;
      } finally {
        setIsMutating(false);
      }
    },
    [apiClients, sphereId, directSubscriptions, refetch, t]
  );

  return {
    effectiveSubscriptions,
    directSubscriptions,
    loading,
    error,
    isMutating,
    subscribe,
    unsubscribe,
    updateScope,
    refetch,
  };
}
